import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid2';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import React from 'react';

import { DimensionsService, type TimeDimension } from '~/services/dimensions';
import { ExportService, type Version } from '~/services/exports';

interface VersionFormProps {
  onSelect: (selected: { version: Version; timeDimension: TimeDimension }) => void;
}

interface VersionFormState {
  selectedTimeDimension: TimeDimension;
  timeDimensions: TimeDimension[];
  selectedVersion: Version | undefined;
  versions: Version[];
}

export default class VersionFormComponent extends React.Component<VersionFormProps, VersionFormState> {
  constructor(props: VersionFormProps) {
    super(props);
    this.state = {
      timeDimensions: [],
      versions: [],
      selectedTimeDimension: { id: -1, name: 'Loading...' },
      selectedVersion: undefined,
    };
  }

  private find<T extends { id: number }>(array: T[], id: number): T | undefined {
    return array.find((x) => x.id === id);
  }

  private async handleTimeDimensionChange(change: SelectChangeEvent<number>): Promise<void> {

    const selected = this.find(this.state.timeDimensions, change.target.value as number);

    if (!selected) {
      return;
    }

    const versions = await ExportService.getTimeDimensionVersions(selected.name);

    this.setState({
      versions,
      selectedTimeDimension: selected,
      selectedVersion: versions.length > 0 ? versions[versions.length - 1] : undefined,
    });

    if (this.state.selectedVersion) {
      this.props.onSelect({
        timeDimension: selected,
        version: versions[versions.length - 1],
      });
    }
  }

  private handleVersionChange(change: SelectChangeEvent<number | '~'>): void {
    const selected = this.find(
      this.state.versions,
      change.target.value === '~' ? 1 : (change.target.value as number),
    );

    if (!selected) {
      return;
    }

    this.setState({ selectedVersion: selected });

    if (this.state.selectedTimeDimension) {
      this.props.onSelect({
        timeDimension: this.state.selectedTimeDimension,
        version: selected,
      });
    }
  }

  public async componentDidMount(): Promise<void> {
    const submissions = await DimensionsService.getSubmissionYears();
    const versions = await ExportService.getTimeDimensionVersions(submissions[0].name);

    this.setState({
      timeDimensions: submissions,
      selectedTimeDimension: submissions[0],
      versions,
      selectedVersion: versions.length > 0 ? versions[versions.length - 1] : undefined,
    });

    if (versions.length > 0) {
      this.props.onSelect({
        timeDimension: submissions[0],
        version: versions[versions.length - 1],
      });
    }
  }

  public render(): React.ReactNode {
    return (
      <Grid container spacing={2}>
        <Grid size={{ sm: 12, md: 8 }}>
          <FormControl variant="standard" style={{ width: '100%' }}>
            <InputLabel htmlFor="submission-year-picker">Submissionsår</InputLabel>
            <Select
              variant="standard"
              onChange={(e: SelectChangeEvent<number>) => this.handleTimeDimensionChange(e)}
              value={this.state.selectedTimeDimension.id === -1 ? '' : this.state.selectedTimeDimension.id}
              inputProps={{
                name: 'year',
                id: 'submission-year-picker',
              }}
            >
              {this.state.timeDimensions.map((td) => {
                return (
                  <MenuItem value={td.id} key={td.id}>
                    {td.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ sm: 12, md: 4 }}>
          <FormControl variant="standard" style={{ width: '100%' }}>
            <InputLabel htmlFor="submission-year-picker">Version</InputLabel>
            <Select
              variant="standard"
              onChange={(e: SelectChangeEvent<number | '~'>) => this.handleVersionChange(e)}
              value={this.state.selectedVersion ? this.state.selectedVersion.id : ''}
              inputProps={{
                name: 'version',
                id: 'submission-year-version-picker',
              }}
            >
              <MenuItem value="" disabled={true}>
                Ingen version
              </MenuItem>
              {this.state.versions.map((v) => (
                <MenuItem value={v.id} key={v.id}>
                  {v.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    );
  }
}
